import { api, readTotalCount } from '../api/client';
import { API_PACKAGES_URL } from '../utils';

// Discover's ranked search (KPR-1437; keepr-api docs/SCHEMA.md "Ranked
// search", "Global collections in the same list" and the packages subject's
// `publisher` and `examples`, KPR-1432 / KPR-1434 / KPR-1446). Its own module:
// src/services/packages.js is held by other builds. Errors propagate; the page
// words them.

/** A page of Discover: 50 rows, paged by `skip` until `X-Total-Count`. */
export const DISCOVER_PAGE_SIZE = 50;

/**
 * `{ rows, total }` — one page of `GET /api/packages`. `search` is the words a
 * person typed (ranked best match first, each row with `matched`); with words,
 * global collections come in the same list (`include=collections`, refused by
 * the API without a search). `q` is KQL over the packages subject; `sort` one
 * of SORTS' values.
 */
export async function searchDiscover({ search = '', q = '', sort = '', skip = 0, limit = DISCOVER_PAGE_SIZE, goesWith = '' } = {}, { signal } = {}) {
	const params = { limit };
	if (goesWith) params.goesWith = goesWith;
	const words = `${search || ''}`.trim();
	if (words) {
		params.search = words;
		params.include = 'collections';
	}
	if (q) params.q = q;
	if (sort) params.sort = sort;
	if (skip) params.skip = skip;
	const { data, headers } = await api.get(API_PACKAGES_URL, signal ? { params, signal } : { params });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, skip + rows.length) };
}

// ---- Browse rows (KPR-1438) ----

/** How many packages each browse row on the front page shows. */
export const BROWSE_ROW_SIZE = 8;

/**
 * The three rows under Featured on the unfiltered page, each read on its own
 * so one failing leaves the others: Most added (`sort=installs`, only what has
 * been added at least once), Recently updated (`sort=updated`) and Goes with
 * what you have (`goesWith=mine`, keepr-api docs/SCHEMA.md "Goes with what you
 * have"). A read that fails, or an API without `goesWith`, is an empty row,
 * and an empty row is not drawn.
 */
export async function loadBrowseRows() {
	const read = params => searchDiscover({ ...params, limit: BROWSE_ROW_SIZE }).then(({ rows }) => rows).catch(() => []);
	const [mostAdded, recentlyUpdated, goesWith] = await Promise.all([
		read({ sort: 'installs' }),
		read({ sort: 'updated' }),
		read({ goesWith: 'mine' }),
	]);
	return {
		mostAdded: mostAdded.filter(row => Number(row.installs) > 0),
		recentlyUpdated,
		// Every row of a goesWith answer says why; one that does not is not this API's.
		goesWith: goesWith.filter(row => row.reason),
	};
}

/**
 * A Goes with row's one muted line: what it works with ("Goes with Person,
 * Atlas") or where add-ons wait ("Add-ons to add in Home: Fuel log").
 */
export function goesWithLine(row) {
	const named = Array.isArray(row?.named) ? row.named : [];
	if (!named.length) return '';
	if (row.reason === 'add_ons') {
		return `Add-ons to add in ${named.map(entry => `${entry.name}: ${(entry.addOns || []).join(', ')}`).join(' · ')}`;
	}
	return `Goes with ${named.map(entry => entry.name).filter(Boolean).join(', ')}`;
}

// ---- Sort ----

/** The Sort select, in order. `relevance` is offered only while searching. */
export const SORTS = [
	{ value: 'relevance', label: 'Best match', searchOnly: true },
	{ value: 'featured', label: 'Featured' },
	{ value: 'installs', label: 'Most added' },
	{ value: 'newest', label: 'Newest' },
	{ value: 'updated', label: 'Recently updated' },
	{ value: 'name', label: 'Name' },
];

/** The sorts a person may pick: Best match only with words. */
export const sortOptions = searching => SORTS.filter(sort => searching || !sort.searchOnly);

/**
 * The sort in force, from the URL's `sort`: Best match by default while
 * searching, Featured (the curated page) otherwise. A value that is not a
 * sort, or Best match without words, is the default.
 */
export function effectiveSort(sortParam, searching) {
	const known = SORTS.find(sort => sort.value === sortParam);
	if (!known || (known.searchOnly && !searching)) return searching ? 'relevance' : 'featured';
	return known.value;
}

/**
 * What the request sends as `sort`: nothing for Best match (the API's default
 * with words). Without words Featured is still sent: the curated page groups
 * what it reads, and the Featured row's packages come first, on the first page.
 */
export function requestSort(sort, searching) {
	if (searching && sort === 'relevance') return '';
	return sort || '';
}

// ---- Facets ----

export const KIND_OPTIONS = [
	{ value: 'collection', label: 'Collection' },
	{ value: 'bundle', label: 'Card bundle' },
	{ value: 'card', label: 'Card' },
];

const KINDS = new Set(KIND_OPTIONS.map(option => option.value));

/** A KQL string literal: the one escape rule, `\"` and `\\`. */
export const kqlString = value => `"${`${value}`.replace(/["\\]/g, match => `\\${match}`)}"`;

/** The facets the URL holds: `category`, `kind`, `examples=1`, `publisher`. */
export function readFacets(params) {
	const get = name => (params.get(name) || '').trim();
	const kind = get('kind');
	return {
		category: get('category'),
		kind: KINDS.has(kind) ? kind : '',
		examples: get('examples') === '1',
		publisher: get('publisher'),
	};
}

/** How many facets are set: the Filters button's count. */
export const facetCount = facets => ['category', 'kind', 'examples', 'publisher'].filter(name => Boolean(facets[name])).length;

/**
 * The request's `q`: each facet set, ANDed, and an older link's `q` (kept as
 * the person sent it) in parentheses before them. '' when nothing is set.
 */
export function facetsToQuery(facets = {}, legacyQ = '') {
	const parts = [];
	if (legacyQ) parts.push(`(${legacyQ})`);
	if (facets.category) parts.push(`category = ${kqlString(facets.category)}`);
	if (facets.kind) parts.push(`kind = ${facets.kind}`);
	if (facets.examples) parts.push('examples = true');
	if (facets.publisher) parts.push(`publisher = ${kqlString(facets.publisher)}`);
	return parts.join(' and ');
}

/**
 * The publisher's key of a package row: its key's prefix (`acme/orders` is
 * acme's), `keepr` for a bare key — what the packages subject's `publisher`
 * reads (KPR-1446). A collection row has none.
 */
export function publisherKeyOf(row) {
	if (!row || row.type === 'collection' || typeof row.key !== 'string' || !row.key) return '';
	const slash = row.key.indexOf('/');
	return slash > 0 ? row.key.slice(0, slash) : 'keepr';
}

/**
 * The Publisher facet's options, learned from the rows seen: `{ value: key,
 * label: name }`, keepr first, then by name. `known` (a Map kept by the page)
 * keeps every publisher seen so far, so narrowing never empties the picker;
 * `selected` stays listed even when no row has shown it yet.
 */
export function publisherOptions(rows, known = new Map(), selected = '') {
	(rows || []).forEach(row => {
		const key = publisherKeyOf(row);
		if (!key) return;
		const name = row.publisher?.name || (key === 'keepr' ? 'keepr' : key);
		if (!known.has(key) || known.get(key) === key) known.set(key, name);
	});
	if (selected && !known.has(selected)) known.set(selected, selected);
	return [...known.entries()]
		.map(([value, label]) => ({ value, label }))
		.sort((a, b) => (a.value === 'keepr' ? -1 : b.value === 'keepr' ? 1 : a.label.localeCompare(b.label)));
}

// ---- Why it matched ----

const QUOTED = text => `\u201c${text}\u201d`;

/** One reason, `{ field, text }` as the API serves it, in a person's words. */
export function matchReason(reason) {
	const text = `${reason?.text || ''}`.trim();
	if (!text) return '';
	switch (reason.field) {
		case 'name': return `the name ${QUOTED(text)}`;
		case 'keyword': return `keyword ${QUOTED(text)}`;
		case 'goodFor': return `good for ${QUOTED(text)}`;
		case 'listing': return text;
		case 'card': return `${text}, a card`;
		case 'addOn': return `${text}, an add-on`;
		case 'element': {
			// "VIN on Vehicle" → "VIN, an element on Vehicle"; an element set's
			// "Colour in Paint details" has no card to name.
			const at = text.lastIndexOf(' on ');
			return at > 0 ? `${text.slice(0, at)}, an element on ${text.slice(at + 4)}` : `${text}, an element`;
		}
		case 'description': return `the description: ${QUOTED(text)}`;
		case 'exampleSet': return `${text}, an example set`;
		case 'cardDescription': return `a card's description: ${QUOTED(text)}`;
		case 'category': return `the category ${text}`;
		default: return text;
	}
}

/**
 * A ranked row's one muted line — "Matched: VIN, an element on Vehicle" — or
 * '' for a row the search did not rank (no `matched`).
 */
export function matchLine(row) {
	const reasons = (Array.isArray(row?.matched) ? row.matched : []).slice(0, 3).map(matchReason).filter(Boolean);
	return reasons.length ? `Matched: ${reasons.join(' · ')}` : '';
}
